import React, { createContext, useEffect, useRef, useState } from 'react'
import { Cookies, useCookies } from 'react-cookie'
import { cookieOptions, parseSessionToken, removeBearerFromToken } from './CookieUtils'
import Profile from 'components/common/types/Profile'
import { useHistory } from 'react-router'
import axios from 'axios'
import { I18nLoader } from 'components/I18nLoader'
import { get, isEmpty } from 'lodash'
import { clearSessionScopedCookies } from './sessionScopedCookies'
import { reloadPage } from './pageReload'
import { useQueryClient } from '@tanstack/react-query'
import { currentSessionToken, hasRefreshHint, isExpiredToken, isLegacyToken, isUsableToken, onSessionRefresh, refreshSession, REFRESH_URL } from './sessionRefresh'

interface SessionValues {
  sessionToken: string
  myProfile: Profile
}

interface SessionContextProps {
  handleLogout: (redirect?: boolean) => void
  updateSession: (sessionValues: SessionValues, reactive: boolean) => void
  refetchSession: () => void
  session: {
    redirectToLoginIfNoSession: (redirectUri: string) => boolean
    isValid: () => boolean
    sessionToken?: string
  }
  myProfile?: Profile
  sessionToken?: string
}

export const SessionContext = createContext<SessionContextProps>({
  handleLogout: () => {},
  updateSession: (_sessionValues) => {},
  refetchSession: () => {},
  session: {
    redirectToLoginIfNoSession: (_redirectUri) => { return false },
    isValid: () => { return false },
    sessionToken: undefined
  },
  myProfile: undefined,
  sessionToken: undefined
})

export const SessionProvider: React.FC<React.PropsWithChildren> = (props) => {
  const { children } = props

  const queryClient = useQueryClient()
  const [myProfile, setMyProfile] = useState<Profile | undefined>(undefined)
  const history = useHistory()
  const [cookies, setCookie] = useCookies(['sessionToken'])
  const token: string | undefined = parseSessionToken(cookies.sessionToken)
  const needsRefresh = (value?: string): boolean => isEmpty(value) || isExpiredToken(value) || isLegacyToken(value)
  const [ready, setReady] = useState<boolean>(() => !needsRefresh(token) || (isEmpty(token) && !hasRefreshHint()))
  const ended = useRef<boolean>(false)
  const endSessionRef = useRef<() => void>(() => {})
  const tokenRef = useRef<string | undefined>(token)
  tokenRef.current = token

  const updateTokenCookie = (token: string, reactive: boolean = true): void => {
    if (reactive) {
      setCookie('sessionToken', token, cookieOptions())
      return
    }
    (new Cookies()).set('sessionToken', token, cookieOptions())
  }

  const fixCookieEncoding = (token: string): string => {
    // If the token is already valid, just return it
    if (!token.startsWith('Bearer+')) return token

    // Replace the + with a space. Rails cookies are auto encoded, and it
    // encodes the ' ' as a '+', instead of a '%20'. Hence this correction.
    token = token.replace('Bearer+', 'Bearer ')

    // Make sure that the token in the cookies is also fine.
    updateTokenCookie(token)
    return token
  }

  const isValid = (): boolean => { return Boolean(token) }

  const redirectToLoginIfNoSession = (redirectUri: string = window.location.href): boolean => {
    if (isValid()) return false

    const encodedRedirectUri: string = encodeURIComponent(redirectUri)

    let url: string = '/login'
    if (url !== '') url += `?redirect_uri=${encodedRedirectUri}`
    history.push(url)
    return true
  }

  const revokeRefreshToken = async (): Promise<void> => {
    await axios({ method: 'DELETE', url: REFRESH_URL, headers: { 'X-Requested-With': 'XMLHttpRequest' } }).catch(err => console.log(err))
  }

  const logout = (redirect: boolean = true): void => {
    // No cookie, or the '' left behind by a previous logout: nothing to revoke.
    if (!token) {
      void revokeRefreshToken()
      return
    }

    clearSessionScopedCookies()

    revokeRefreshToken().then(async () => await axios({ method: 'DELETE', url: '/api/v1/logout', headers: { Authorization: token, 'Content-Type': 'application/json' } })).then((_response) => {
      updateTokenCookie('')
      I18nLoader.load() // ensure locale is reset to browser default
      if (redirect) {
        history.push('/')
      }
    }).catch(err => {
      console.log(err)
      updateTokenCookie('')
      if (redirect) {
        history.push('/')
        reloadPage()
      }
    }).finally(() => {
      queryClient.removeQueries()
    })
    // TODO Maybe remove the token in the .finally method?
  }

  const fetchMyProfile = (): void => {
    if (token === undefined) return

    axios({
      method: 'GET',
      url: '/api/v1/profiles/my',
      headers: { Authorization: token, 'Content-Type': 'application/json' }
    }).then((response) => {
      // If the names are missing, we set them to the empty string, so that any
      // text fields that require a string still work, and so that it doesn't render
      // as "null" anywhere, and so that a check on if (first_name) will render false,
      // indicating to the ProfileCompleteness component that the first_name or last_name
      // is missing if it is.
      const profile: Profile = response.data
      profile.first_name = response.data.first_name ?? ''
      profile.last_name = response.data.last_name ?? ''
      if (!isEmpty(profile.locale)) {
        I18nLoader.loadFromGiven(profile.locale)
      }
      setMyProfile(profile)
    }).catch((err) => {
      /* We only want to logout the user if we did get a reply from the backend
       *  (like a 400 or 401). If the user's internet dropped for a second, we
       * don't want to log them out. We also don't want to logout the user if the
       * authentication failed because of updated terms of service. This is handled by a
       * global axios error handler instead. */
      const errorKey: string | undefined = get(err, 'response.data.error_key', undefined)
      if (errorKey === 'tos_required') {
        return
      }
      // Wrong cookie or cookie expired. A dropped connection rejects without a
      // response at all, so read the status defensively and log nobody out.
      if (get(err, 'response.status', undefined) === 401) {
        console.log('Error retrieving profile, logging out:')
        endSessionRef.current()
      }
    })
  }

  const updateSession = (sessionValues: SessionValues, reactive: boolean = true): void => {
    updateTokenCookie(sessionValues.sessionToken, reactive)
    if (reactive) setMyProfile(sessionValues.myProfile)
  }

  const endSession = (): void => {
    if (ended.current) return

    ended.current = true
    logout(false)
    updateTokenCookie('')
    if (window.location.pathname !== '/login') {
      history.push(`/login?redirect_uri=${encodeURIComponent(window.location.href)}`)
    }
  }
  endSessionRef.current = endSession

  useEffect(() => onSessionRefresh((refreshed) => {
    const latest = currentSessionToken()
    if (refreshed !== undefined) {
      updateTokenCookie(refreshed)
    } else if (latest !== tokenRef.current && isUsableToken(latest)) {
      updateTokenCookie(latest as string)
    } else if (latest !== undefined) {
      endSessionRef.current()
    }
  }), [])

  useEffect(() => {
    if (ready) return

    refreshSession(token).catch(err => console.log(err)).finally(() => setReady(true))
  }, [])

  useEffect(() => {
    if (!isEmpty(token)) ended.current = false
  }, [token])

  // Fixes the encoding of the token, sets it, and reloads the component.
  useEffect(() => {
    token !== undefined && fixCookieEncoding(token)
  }, [token])

  useEffect(() => {
    ready && isValid() && !needsRefresh(token) && fetchMyProfile()
  }, [token, ready])

  return (
    <SessionContext.Provider value={{
      handleLogout: logout,
      updateSession,
      refetchSession: fetchMyProfile,
      session: {
        redirectToLoginIfNoSession,
        isValid,
        sessionToken: removeBearerFromToken(token)
      },
      myProfile,
      // This token includes the "Bearer" at the beginning
      sessionToken: token
    }}
    >
      {ready && children}
    </SessionContext.Provider>
  )
}

export const WithSession = (Component: React.ElementType): React.ForwardRefExoticComponent<React.RefAttributes<React.ElementType>> => React.forwardRef<React.ElementType>((props, ref) => {
  return (
    <SessionContext.Consumer>
      {(context) => (<Component ref={ref} {...context} {...props} />)}
    </SessionContext.Consumer>
  )
})
